import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, collection, getDocs, addDoc, updateDoc, deleteDoc, query, orderBy, serverTimestamp
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from '../firebase/firebase';
import { ArrowLeft, Eye, Pencil, Trash2, ChevronLeft, ChevronRight, RefreshCw, Archive } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import withProjectPermissions from '../components/withProjectPermissions';
import DrawingViewer from '../components/DrawingViewer';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const DesignLanding = ({ permissions }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [designFiles, setDesignFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Upload modal (also used for revision updates - updateTargetId set means "update" mode)
  const [modalOpen, setModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileComment, setFileComment] = useState('');
  const [uploading, setUploading] = useState(false);
  const [updateTargetId, setUpdateTargetId] = useState(null);
  const [, setError] = useState('');
  const [, setSuccess] = useState('');

  // Active / Archived tabs
  const [activeTab, setActiveTab] = useState('active');

  // Inline edit
  const [editingIdx, setEditingIdx] = useState(null);
  const [editName, setEditName] = useState('');
  const [editComment, setEditComment] = useState('');

  // UI
  const [deleteIdx, setDeleteIdx] = useState(null);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [viewer, setViewer] = useState({ open: false, file: null });
  const [tilePageStates, setTilePageStates] = useState({});
  const [pdfPageCounts, setPdfPageCounts] = useState({});

  const auth = getAuth();
  const user = auth.currentUser;


  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) return;
      try {
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        if (projectDoc.exists()) setProject({ id: projectDoc.id, ...projectDoc.data() });
      } catch (err) {
        console.error('Error fetching project:', err);
      }
    };
    fetchProject();
  }, [projectId]);

  useEffect(() => {
    const fetchDesignFiles = async () => {
      if (!projectId) return;
      try {
        setLoading(true);
        const q = query(collection(db, `projects/${projectId}/designs`), orderBy('uploadedAt', 'desc'));
        const snap = await getDocs(q);
        const data = snap.docs.map(d => ({
          id: d.id,
          ...d.data(),
          uploadedAt: d.data().uploadedAt?.toDate ? d.data().uploadedAt.toDate() : new Date(d.data().uploadedAt),
        }));
        setDesignFiles(data);
      } catch (err) {
        console.error('Error fetching design files:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDesignFiles();
  }, [projectId, uploading, editingIdx, deleteIdx]);

  // Closed projects and non-editors (viewers, company owners without collaborator status)
  // are both read-only here, just for different reasons.
  const isReadOnly = project?.status === 'closed' || !permissions?.canEdit;

  const handleOpenModal = () => {
    if (isReadOnly) return;
    setModalOpen(true);
    setUpdateTargetId(null);
    setError('');
    setSuccess('');
    setFile(null);
    setFileName('');
    setFileComment('');
  };

  const handleOpenUpdateModal = (df) => {
    if (project?.status === 'closed') {
      alert('This project is closed. You cannot update drawings.');
      return;
    }
    if (!permissions?.canEdit) {
      alert('You do not have permission to edit this project. Only the project owner or invited PM collaborators can make changes.');
      return;
    }
    setModalOpen(true);
    setUpdateTargetId(df.id);
    setError('');
    setSuccess('');
    setFile(null);
    setFileName('');
    setFileComment('');
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setUpdateTargetId(null);
    setError('');
    setFile(null);
    setFileName('');
    setFileComment('');
  };

  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
    setFileName(selectedFile ? selectedFile.name : '');
    setError('');
    setSuccess('');
  };

  const handleUpload = async () => {
    setError('');
    setSuccess('');
    if (!file) { setError('Please select a file.'); return; }
    setUploading(true);
    try {
      const storage = getStorage();
      const storageRef = ref(storage, `projects/${projectId}/designs/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      if (updateTargetId) {
        // Revision update: create the new doc, then archive the one it replaces.
        // Never overwrite/delete the old doc - superseded revisions are retained for
        // traceability (matches the existing Documents archive pattern).
        const targetDrawing = designFiles.find(d => d.id === updateTargetId);
        const newDocRef = await addDoc(collection(db, `projects/${projectId}/designs`), {
          url, name: fileName || file.name, comment: fileComment || '',
          uploadedAt: serverTimestamp(), uploadedBy: user?.uid || 'unknown',
          fileName: file.name, fileType: file.type, fileSize: file.size,
          status: 'active',
          previousRevisionId: updateTargetId,
          revisionNumber: (targetDrawing?.revisionNumber || 1) + 1,
        });
        await updateDoc(doc(db, `projects/${projectId}/designs`, updateTargetId), {
          status: 'archived',
          archivedAt: serverTimestamp(),
          archivedBy: user?.uid || 'unknown',
          supersededByRevisionId: newDocRef.id,
        });
        setSuccess('Drawing updated!');
      } else {
        await addDoc(collection(db, `projects/${projectId}/designs`), {
          url, name: fileName || file.name, comment: fileComment || '',
          uploadedAt: serverTimestamp(), uploadedBy: user?.uid || 'unknown',
          fileName: file.name, fileType: file.type, fileSize: file.size,
          status: 'active',
          revisionNumber: 1,
        });
        setSuccess('File uploaded!');
      }

      setFile(null); setFileName(''); setFileComment(''); setModalOpen(false); setUpdateTargetId(null);
    } catch (err) {
      setError('Upload failed. Please try again.');
      console.error(err);
    }
    setUploading(false);
  };

  const handleEdit = (idx) => {
    if (project?.status === 'closed') {
      alert('This project is closed. You cannot edit drawings.');
      return;
    }
    if (!permissions?.canEdit) {
      alert('You do not have permission to edit this project. Only the project owner or invited PM collaborators can make changes.');
      return;
    }
    setEditingIdx(idx);
    setEditName(designFiles[idx].name);
    setEditComment(designFiles[idx].comment || '');
  };

  const handleEditSave = async (idx) => {
    const df = designFiles[idx];
    await updateDoc(doc(db, `projects/${projectId}/designs`, df.id), {
      name: editName, comment: editComment, updatedAt: serverTimestamp(),
    });
    setEditingIdx(null);
  };

  const handleDelete = (idx) => {
    if (isReadOnly) return;
    setDeleteIdx(idx);
  };
  const cancelDelete = () => setDeleteIdx(null);
  const confirmDelete = async () => {
    if (deleteIdx === null) return;
    await deleteDoc(doc(db, `projects/${projectId}/designs`, designFiles[deleteIdx].id));
    setDeleteIdx(null);
  };

  const handleTilePrevPage = (fileUrl, currentPage) => {
    if (currentPage > 1) setTilePageStates(prev => ({ ...prev, [fileUrl]: currentPage - 1 }));
  };

  const handleTileNextPage = (fileUrl, currentPage) => {
    const total = pdfPageCounts[fileUrl] || 1;
    if (currentPage < total) setTilePageStates(prev => ({ ...prev, [fileUrl]: currentPage + 1 }));
  };

  const openViewer = (f) => setViewer({ open: true, file: f });
  const closeViewer = () => setViewer({ open: false, file: null });

  // Scale ruler: persist one page's calibration on the design doc without clobbering
  // other pages' calibrations (dotted field path = merge at that key only).
  const handleSaveCalibration = async ({ pageIndex, referenceStart, referenceEnd, realLength, unit }) => {
    const fileId = viewer.file?.id;
    if (!fileId) return;
    const entry = {
      referenceStart,
      referenceEnd,
      realLength,
      unit,
      calibratedBy: user?.uid || null,
      calibratedAt: serverTimestamp(),
    };
    await updateDoc(doc(db, 'projects', projectId, 'designs', fileId), {
      [`calibration.${pageIndex}`]: entry,
    });
    const nextCalibration = { ...(viewer.file.calibration || {}), [pageIndex]: entry };
    setViewer(prev => ({ ...prev, file: { ...prev.file, calibration: nextCalibration } }));
    setDesignFiles(prev => prev.map(f => (f.id === fileId ? { ...f, calibration: nextCalibration } : f)));
  };

  if (!project) return <p style={{ padding: '40px' }}>Loading project...</p>;
  if (loading) return <p style={{ padding: '40px' }}>Loading drawings...</p>;

  // Pre-existing drawings (uploaded before this feature) have no status field at all -
  // treat that as active, same loose comparison DocumentLanding.jsx already uses.
  const activeDrawings = designFiles.filter(d => d.status !== 'archived');
  const archivedDrawings = designFiles.filter(d => d.status === 'archived');
  const visibleFiles = activeTab === 'archived' ? archivedDrawings : activeDrawings;

  return (
    <div style={{ padding: '40px', backgroundColor: 'var(--branding-app-surface)', minHeight: '100vh' }}>
      {/* Back button */}
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => navigate(`/project/${projectId}`)}
          style={{
            background: 'transparent', border: 'none',
            color: 'var(--cc-text-muted)', cursor: 'pointer',
            padding: '8px', borderRadius: '6px',
            display: 'flex', alignItems: 'center', gap: '6px',
            fontSize: '14px', fontWeight: '500',
          }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--branding-app-primary)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--cc-text-muted)'}
        >
          <ArrowLeft size={20} /> Back to project
        </button>
      </div>

      {/* Header block */}
      <h1 style={{ fontSize: 'clamp(20px, 2.2vw, 22px)', fontWeight: 600, color: 'var(--cc-text-primary)', marginBottom: 24 }}>
        {project.name}
      </h1>
      {project.projectCode && (
        <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 12, lineHeight: 1.4 }}>
          <strong>Project Code:</strong> {project.projectCode}
        </p>
      )}
      <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 16, lineHeight: 1.4 }}>
        <strong>Address:</strong> {project.address}
      </p>
      <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 24, lineHeight: 1.4 }}>
        <strong>Postcode:</strong> {project.postcode}
      </p>

      {/* Main card */}
      <div style={{
        background: 'var(--cc-card-bg)',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        maxWidth: '1200px',
        margin: '0 auto',
        width: '90%',
      }}>
        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--cc-text-primary)', margin: 0 }}>
            Drawings
          </h2>
          {activeDrawings.length > 0 && (
            <span style={{
              marginLeft: 10,
              background: 'var(--branding-app-surface-panel)',
              color: 'var(--cc-text-muted)',
              fontSize: 12, fontWeight: 600,
              padding: '2px 10px', borderRadius: 20,
            }}>
              {activeDrawings.length}
            </span>
          )}
          {activeTab !== 'archived' && (
            <div style={{ marginLeft: 'auto' }}>
              <button
                onClick={() => {
                  if (project?.status === 'closed') {
                    alert('This project is closed. You cannot upload new drawings.');
                    return;
                  }
                  if (!permissions?.canEdit) {
                    alert('You do not have permission to edit this project. Only the project owner or invited PM collaborators can make changes.');
                    return;
                  }
                  handleOpenModal();
                }}
                disabled={isReadOnly}
                style={{
                  background: isReadOnly ? '#cbd5e1' : 'var(--branding-app-primary)',
                  color: isReadOnly ? '#64748b' : 'var(--branding-app-primary-fg)',
                  border: 'none', borderRadius: '8px',
                  padding: '9px 20px',
                  fontWeight: 600, cursor: isReadOnly ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  opacity: isReadOnly ? 0.6 : 1,
                }}
              >
                + Add Drawing
              </button>
            </div>
          )}
        </div>

        {/* Archived toggle — only shown once there's actually something archived */}
        {archivedDrawings.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <button
              type="button"
              onClick={() => setActiveTab(activeTab === 'archived' ? 'active' : 'archived')}
              style={{
                padding: '8px 16px', fontSize: 14, fontWeight: 500,
                color: activeTab === 'archived' ? 'var(--branding-app-primary)' : 'var(--cc-text-muted)',
                background: 'transparent',
                border: '1px solid var(--branding-app-surface-panel-border)',
                borderRadius: 8,
                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <Archive size={14} />
              {activeTab === 'archived' ? 'Back to drawings' : `Archived (${archivedDrawings.length})`}
            </button>
          </div>
        )}

        {/* Closed notice */}
        {project?.status === 'closed' && activeTab !== 'archived' && (
          <div style={{
            background: '#fef3c7', border: '1px solid #f59e0b',
            borderRadius: 8, padding: '14px 18px', marginBottom: 24,
            color: '#92400e', fontSize: 14, fontWeight: 500,
          }}>
            This project is closed and read-only. Drawings can be viewed but not uploaded or edited.
          </div>
        )}

        {/* Tile grid / empty state */}
        {visibleFiles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '64px 20px', color: 'var(--cc-text-muted)', fontSize: 14 }}>
            {activeTab === 'archived' ? 'No archived drawings' : 'No drawings yet'}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 24,
          }}>
            {visibleFiles.map((df) => {
              const idx = designFiles.findIndex(d => d.id === df.id);
              // df.name is a user-editable display title, not reliably the real filename
              // (e.g. renamed to "Ground Floor Plan Rev 3" with no extension) — fall back to
              // the MIME type and the real fileName saved at upload time, same as DrawingViewer.
              const isImage = /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(df.name || '') ||
                /^image\//i.test(df.fileType || '') ||
                /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(df.fileName || '');
              const isPDF = /\.pdf$/i.test(df.name || '') ||
                df.fileType === 'application/pdf' ||
                /\.pdf$/i.test(df.fileName || '');
              const isDWG = /\.dwg$/i.test(df.name || '') || /\.dwg$/i.test(df.fileName || '');
              const isHovered = hoveredIdx === idx;
              const isEditing = editingIdx === idx;
              const isArchived = df.status === 'archived';
              const supersededByName = isArchived
                ? designFiles.find(d => d.id === df.supersededByRevisionId)?.name
                : null;

              let uploadDate = null;
              if (df.uploadedAt) {
                uploadDate = df.uploadedAt instanceof Date ? df.uploadedAt
                  : df.uploadedAt?.toDate ? df.uploadedAt.toDate()
                  : new Date(df.uploadedAt);
              }

              return (
                <div
                  key={df.id || idx}
                  style={{
                    background: 'var(--cc-card-bg)',
                    borderRadius: 12,
                    overflow: 'hidden',
                    border: '1px solid var(--branding-app-surface-panel-border)',
                    boxShadow: isHovered && !isEditing ? '0 8px 28px rgba(0,0,0,0.1)' : '0 2px 8px rgba(0,0,0,0.04)',
                    transform: isHovered && !isEditing ? 'translateY(-2px)' : 'none',
                    transition: 'box-shadow 0.2s, transform 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  onMouseEnter={() => setHoveredIdx(idx)}
                  onMouseLeave={() => setHoveredIdx(null)}
                >
                  {/* Preview area */}
                  <div
                    style={{
                      position: 'relative',
                      height: 220,
                      overflow: 'hidden',
                      background: 'var(--branding-app-surface-panel)',
                      cursor: isEditing ? 'default' : 'pointer',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onClick={() => !isEditing && openViewer(df)}
                  >
                    {isImage && (
                      <img
                        src={df.url}
                        alt={df.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    )}
                    {isPDF && (
                      <Document
                        file={df.url}
                        onLoadSuccess={({ numPages }) => {
                          if (!pdfPageCounts[df.url]) {
                            setPdfPageCounts(prev => ({ ...prev, [df.url]: numPages }));
                          }
                        }}
                      >
                        <Page
                          pageNumber={tilePageStates[df.url] || 1}
                          width={300}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                        />
                      </Document>
                    )}
                    {isDWG && (
                      <span style={{ fontSize: 52, fontWeight: 800, color: '#f59e42', letterSpacing: -2, pointerEvents: 'none' }}>DWG</span>
                    )}
                    {!isImage && !isPDF && !isDWG && (
                      <span style={{ fontSize: 40, color: 'var(--cc-text-muted)', fontWeight: 600, pointerEvents: 'none' }}>FILE</span>
                    )}

                    {/* File type badge */}
                    <div style={{
                      position: 'absolute', top: 10, left: 10,
                      background: isPDF ? 'rgba(239,68,68,0.88)'
                        : isImage ? 'rgba(16,185,129,0.85)'
                        : isDWG ? 'rgba(245,158,11,0.9)'
                        : 'rgba(100,116,139,0.85)',
                      color: '#fff', fontSize: 10, fontWeight: 700,
                      padding: '3px 8px', borderRadius: 5, letterSpacing: '0.5px',
                      pointerEvents: 'none',
                    }}>
                      {isPDF ? 'PDF' : isImage ? 'IMG' : isDWG ? 'DWG' : 'FILE'}
                    </div>

                    {/* PDF page nav */}
                    {isPDF && (pdfPageCounts[df.url] || 0) > 1 && (
                      <>
                        <button
                          style={{
                            position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                            background: (tilePageStates[df.url] || 1) <= 1 ? 'rgba(156,163,175,0.5)' : 'rgba(0,0,0,0.65)',
                            color: '#fff', border: 'none', borderRadius: '50%',
                            width: 30, height: 30,
                            cursor: (tilePageStates[df.url] || 1) <= 1 ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                          onClick={e => { e.stopPropagation(); handleTilePrevPage(df.url, tilePageStates[df.url] || 1); }}
                        >
                          <ChevronLeft size={16} />
                        </button>
                        <button
                          style={{
                            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                            background: (tilePageStates[df.url] || 1) >= (pdfPageCounts[df.url] || 1) ? 'rgba(156,163,175,0.5)' : 'rgba(0,0,0,0.65)',
                            color: '#fff', border: 'none', borderRadius: '50%',
                            width: 30, height: 30,
                            cursor: (tilePageStates[df.url] || 1) >= (pdfPageCounts[df.url] || 1) ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                          onClick={e => { e.stopPropagation(); handleTileNextPage(df.url, tilePageStates[df.url] || 1); }}
                        >
                          <ChevronRight size={16} />
                        </button>
                        <div style={{
                          position: 'absolute', bottom: 8, right: 8,
                          background: 'rgba(0,0,0,0.55)', color: '#fff',
                          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                          pointerEvents: 'none',
                        }}>
                          {tilePageStates[df.url] || 1} / {pdfPageCounts[df.url]}
                        </div>
                      </>
                    )}

                    {/* Hover open overlay */}
                    {isHovered && !isEditing && (
                      <div style={{
                        position: 'absolute', inset: 0,
                        background: 'rgba(0,0,0,0.28)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <div style={{
                          background: 'rgba(255,255,255,0.95)',
                          color: '#1e293b',
                          borderRadius: 24,
                          padding: '9px 26px',
                          fontWeight: 600, fontSize: 14,
                          display: 'flex', alignItems: 'center', gap: 8,
                          boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                          pointerEvents: 'none',
                        }}>
                          <Eye size={15} /> Open
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Card body */}
                  <div style={{ padding: '14px 16px 10px', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {isEditing ? (
                      <>
                        <input
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          autoFocus
                          style={{
                            fontWeight: 600, fontSize: 14,
                            border: '1px solid var(--branding-app-surface-panel-border)',
                            borderRadius: 6, padding: '6px 10px',
                            width: '100%', boxSizing: 'border-box',
                            background: 'var(--cc-card-bg)',
                            color: 'var(--cc-text-primary)',
                            marginBottom: 6,
                          }}
                        />
                        <input
                          value={editComment}
                          onChange={e => setEditComment(e.target.value)}
                          placeholder="Comment (optional)"
                          style={{
                            fontSize: 13,
                            border: '1px solid var(--branding-app-surface-panel-border)',
                            borderRadius: 6, padding: '6px 10px',
                            width: '100%', boxSizing: 'border-box',
                            background: 'var(--cc-card-bg)',
                            color: 'var(--cc-text-primary)',
                          }}
                        />
                        <div style={{ display: 'flex', gap: 8, marginTop: 10, paddingBottom: 4 }}>
                          <button
                            onClick={() => handleEditSave(idx)}
                            style={{ background: '#22c55e', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingIdx(null)}
                            style={{ background: 'var(--cc-cancel-bg)', color: 'var(--cc-text-primary)', border: 'none', borderRadius: 6, padding: '6px 16px', cursor: 'pointer', fontSize: 13 }}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--cc-text-primary)', lineHeight: 1.4, wordBreak: 'break-word' }}>
                            {df.name}
                          </div>
                          {df.revisionNumber > 1 && (
                            <span style={{
                              flexShrink: 0,
                              fontSize: 10, fontWeight: 700,
                              color: 'var(--branding-app-primary)',
                              background: 'var(--branding-app-surface-panel)',
                              padding: '1px 6px', borderRadius: 4,
                            }}>
                              Rev {df.revisionNumber}
                            </span>
                          )}
                        </div>
                        {df.comment && (
                          <div style={{ color: 'var(--cc-text-secondary)', fontSize: 13, fontStyle: 'italic', lineHeight: 1.4 }}>
                            {df.comment}
                          </div>
                        )}
                        <div style={{ color: 'var(--cc-text-muted)', fontSize: 12, marginTop: 4 }}>
                          {uploadDate ? uploadDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                        </div>
                        {isArchived && (
                          <div style={{ color: '#f59e0b', fontSize: 12, marginTop: 2 }}>
                            Superseded{supersededByName ? ` by "${supersededByName}"` : ''}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Action bar */}
                  {!isEditing && !isArchived && !isReadOnly && (
                    <div style={{ padding: '0 12px 12px', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleOpenUpdateModal(df)}
                        style={{
                          background: 'transparent',
                          color: 'var(--branding-app-primary)',
                          border: '1px solid var(--branding-app-primary)',
                          borderRadius: 6, padding: '5px 12px',
                          cursor: 'pointer', fontSize: 12,
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        <RefreshCw size={12} /> Update
                      </button>
                      <button
                        onClick={() => handleEdit(idx)}
                        style={{
                          background: 'transparent',
                          color: 'var(--cc-text-muted)',
                          border: '1px solid var(--branding-app-surface-panel-border)',
                          borderRadius: 6, padding: '5px 12px',
                          cursor: 'pointer', fontSize: 12,
                          display: 'flex', alignItems: 'center', gap: 4,
                          transition: 'color 0.15s, border-color 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = 'var(--cc-text-primary)'; e.currentTarget.style.borderColor = 'var(--cc-text-muted)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'var(--cc-text-muted)'; e.currentTarget.style.borderColor = 'var(--branding-app-surface-panel-border)'; }}
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(idx)}
                        style={{
                          background: 'transparent',
                          color: '#dc2626',
                          border: '1px solid #fca5a5',
                          borderRadius: 6, padding: '5px 12px',
                          cursor: 'pointer', fontSize: 12,
                          display: 'flex', alignItems: 'center', gap: 4,
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Drawing Modal */}
      {modalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 2000,
        }}>
          <div style={{
            background: 'var(--cc-card-bg)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '600px',
            width: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative',
            boxSizing: 'border-box',
          }}>
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: '24px', paddingBottom: '16px',
              borderBottom: '1px solid var(--branding-app-surface-panel-border)',
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: 'var(--cc-text-primary)' }}>
                {updateTargetId ? 'Update Drawing' : 'Add Drawing'}
              </h2>
              <button
                onClick={handleCloseModal}
                style={{
                  background: 'var(--branding-app-primary)', color: '#fff',
                  border: 'none', borderRadius: '8px', padding: '8px 16px',
                  cursor: 'pointer', fontSize: '14px', fontWeight: '500',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
                Close
              </button>
            </div>

            {/* File selection */}
            {!file ? (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--cc-text-primary)', marginBottom: '16px' }}>
                  Select File
                </h3>
                <input
                  id="design-upload-modal"
                  type="file"
                  accept="image/*,.pdf,.dwg"
                  style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files[0]; if (f) handleFileSelect(f); }}
                />
                <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                  <button
                    onClick={() => document.getElementById('design-upload-modal').click()}
                    style={{
                      background: 'var(--branding-app-primary)', color: '#fff',
                      border: 'none', borderRadius: '8px', padding: '12px 24px',
                      fontWeight: '500', cursor: 'pointer', fontSize: '14px',
                      display: 'inline-flex', alignItems: 'center', gap: '8px',
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7,10 12,15 17,10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Select File
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', color: 'var(--cc-text-muted)', fontSize: '13px' }}>
                  <div style={{ flex: 1, height: '1px', background: 'var(--cc-active-bg)' }}></div>
                  <span style={{ padding: '0 12px' }}>or</span>
                  <div style={{ flex: 1, height: '1px', background: 'var(--cc-active-bg)' }}></div>
                </div>
                <div
                  style={{
                    background: 'var(--branding-app-surface-panel)',
                    borderRadius: '8px', padding: '20px',
                    border: '2px dashed #e2e8f0',
                    textAlign: 'center', color: 'var(--cc-text-muted)',
                    cursor: 'pointer', transition: 'all 0.2s ease',
                  }}
                  onClick={() => document.getElementById('design-upload-modal').click()}
                  onDragOver={e => {
                    e.preventDefault(); e.stopPropagation();
                    e.currentTarget.style.background = '#eff6ff';
                    e.currentTarget.style.borderColor = 'var(--branding-app-primary)';
                  }}
                  onDragLeave={e => {
                    e.preventDefault();
                    e.currentTarget.style.background = 'var(--branding-app-surface-panel)';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }}
                  onDrop={e => {
                    e.preventDefault(); e.stopPropagation();
                    e.currentTarget.style.background = 'var(--branding-app-surface-panel)';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    const accepted = Array.from(e.dataTransfer.files).find(f =>
                      f.type.startsWith('image/') || f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.dwg')
                    );
                    if (accepted) handleFileSelect(accepted);
                  }}
                >
                  <div style={{ fontSize: '16px' }}>
                    {uploading ? 'Uploading...' : 'Drag and drop here'}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--cc-text-primary)', marginBottom: '16px' }}>
                  Selected File
                </h3>
                <div style={{
                  background: 'var(--branding-app-surface-panel)',
                  borderRadius: '12px', padding: '20px',
                  border: '1px solid var(--branding-app-surface-panel-border)',
                  display: 'flex', alignItems: 'center', gap: '16px',
                }}>
                  <div style={{
                    width: '80px', height: '100px',
                    background: 'var(--cc-card-bg)', borderRadius: '8px',
                    border: '1px solid var(--branding-app-surface-panel-border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden',
                  }}>
                    {file.type === 'application/pdf' ? (
                      <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: 'var(--cc-card-bg)' }}>
                        <iframe
                          src={`${URL.createObjectURL(file)}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                          style={{ width: '100%', height: '100%', border: 'none', pointerEvents: 'none', userSelect: 'none' }}
                          title="PDF Preview"
                        />
                        <div style={{
                          position: 'absolute', top: 4, right: 4,
                          background: 'rgba(239,68,68,0.9)', color: '#fff',
                          fontSize: '10px', fontWeight: '600', padding: '2px 4px', borderRadius: '4px',
                        }}>PDF</div>
                      </div>
                    ) : file.type.startsWith('image/') ? (
                      <img src={URL.createObjectURL(file)} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px' }} />
                    ) : file.name.toLowerCase().endsWith('.dwg') ? (
                      <div style={{ fontSize: '28px', color: '#f59e42', fontWeight: '600' }}>DWG</div>
                    ) : (
                      <div style={{ fontSize: '28px', color: 'var(--cc-text-muted)', fontWeight: '600' }}>File</div>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--cc-text-primary)', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {file.name}
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--cc-text-muted)', marginBottom: '8px' }}>
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                    <button
                      onClick={() => { setFile(null); setFileName(''); setFileComment(''); }}
                      style={{
                        background: 'var(--cc-badge-bg)', color: 'var(--cc-text-primary)',
                        border: '1px solid #d1d5db', borderRadius: '6px',
                        padding: '6px 12px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
                      }}
                    >
                      Change File
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Drawing details */}
            {file && (
              <div style={{ marginBottom: '24px', width: '100%', boxSizing: 'border-box' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--cc-text-primary)', marginBottom: '16px' }}>
                  Drawing Details
                </h3>
                <div style={{
                  background: 'var(--branding-app-surface-panel)',
                  borderRadius: '8px', padding: '16px',
                  border: '1px solid var(--branding-app-surface-panel-border)',
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cc-text-primary)', marginBottom: '4px' }}>
                      Drawing Name
                    </label>
                    <input
                      type="text"
                      value={fileName}
                      onChange={e => setFileName(e.target.value)}
                      placeholder="Enter drawing name"
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cc-text-primary)', marginBottom: '4px' }}>
                      Comment (Optional)
                    </label>
                    <textarea
                      value={fileComment}
                      onChange={e => setFileComment(e.target.value)}
                      placeholder={updateTargetId ? 'What changed in this revision? (optional)' : 'Add a comment about this drawing...'}
                      rows={4}
                      style={{
                        width: '100%', padding: '10px 12px',
                        border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14,
                        resize: 'vertical', backgroundColor: 'var(--cc-card-bg)',
                        fontFamily: 'inherit', boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={handleCloseModal}
                style={{ background: 'var(--cc-cancel-bg)', color: 'var(--cc-text-primary)', border: 'none', borderRadius: '8px', padding: '10px 20px', fontWeight: '500', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!file || uploading}
                style={{
                  background: (!file || uploading) ? '#cbd5e1' : 'var(--branding-app-primary)',
                  color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px',
                  fontWeight: '500', cursor: (!file || uploading) ? 'not-allowed' : 'pointer', fontSize: '14px',
                }}
              >
                {updateTargetId
                  ? (uploading ? 'Updating...' : 'Update Drawing')
                  : (uploading ? 'Uploading...' : 'Add Drawing')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteIdx !== null && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
        }}>
          <div style={{ background: 'var(--cc-card-bg)', borderRadius: 12, padding: 32, minWidth: 340, boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>
            <h3 style={{ marginTop: 0, marginBottom: 18, color: '#ef4444' }}>
              Delete <span style={{ fontWeight: 600 }}>{designFiles[deleteIdx]?.name}</span>?
            </h3>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={cancelDelete}
                style={{ background: 'var(--cc-cancel-bg)', color: 'var(--cc-text-primary)', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 500, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drawing viewer */}
      <DrawingViewer
        open={viewer.open}
        onClose={closeViewer}
        file={viewer.file}
        enableCalibration={permissions?.canEdit}
        calibration={viewer.file?.calibration}
        onSaveCalibration={handleSaveCalibration}
      />
    </div>
  );
};

export default withProjectPermissions(DesignLanding, { requireEdit: true });
